/**
 * LocationsSummaryTable — dense, sortable per-location summary.
 *
 * Companion to LocationsGrandTotal (which sums across all rows above this
 * table). Each row is a location; click navigates to that location's
 * dashboard via the parent-supplied onRowClick.
 *
 * Sortable on every column. Default sort: analysis_count DESC (busiest first).
 * Empty / loading states match the visual idiom of ActivityFeedTable and
 * QualificationTab.
 */

import { useMemo, useState } from 'react'
import { formatStartDate } from '@/hooks/useNewSystemStartDates'
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import {
  type GHLLocationOverview,
  formatCost,
  formatTokenCount,
  formatRelativeTime,
  calculateTotalCost,
  truncateId,
} from '@/lib/ghl-dashboard'
import { ArrowUpDown, ArrowUp, ArrowDown, MapPin } from 'lucide-react'

type SortColumn =
  | 'name'
  | 'analyses'
  | 'bookings'
  | 'bookingRate'
  | 'cost'
  | 'tokens'
  | 'pendingFollowups'
  | 'lastActivity'

type SortDirection = 'asc' | 'desc'

interface Props {
  readonly data: readonly GHLLocationOverview[]
  readonly isLoading: boolean
  readonly onRowClick: (locationId: string) => void
  /** Account start dates by location id; those rows are greyed out with the date under the name. */
  readonly startDates?: ReadonlyMap<string, string>
}

/** Per-column sort key extractors. Returning -Infinity / '' for nullable
 * fields keeps them at the bottom in DESC order, top in ASC. */
const comparators: Record<SortColumn, (loc: GHLLocationOverview) => number | string> = {
  name: (loc) => loc.name?.toLowerCase() ?? '',
  analyses: (loc) => loc.analysis_count,
  bookings: (loc) => loc.bookings_detected,
  bookingRate: (loc) =>
    loc.analysis_count > 0 ? loc.bookings_detected / loc.analysis_count : -Infinity,
  cost: (loc) => calculateTotalCost(loc.total_input_tokens, loc.total_output_tokens),
  tokens: (loc) => loc.total_input_tokens + loc.total_output_tokens,
  pendingFollowups: (loc) => loc.pending_followups,
  lastActivity: (loc) =>
    loc.last_analysis_at ? new Date(loc.last_analysis_at).getTime() : -Infinity,
}

function SortIcon({ active, direction }: { active: boolean; direction: SortDirection }) {
  if (!active) return <ArrowUpDown className="h-3 w-3 ml-1 inline opacity-40" />
  return direction === 'asc' ? (
    <ArrowUp className="h-3 w-3 ml-1 inline" />
  ) : (
    <ArrowDown className="h-3 w-3 ml-1 inline" />
  )
}

function SkeletonRow({ cols = 8 }: { cols?: number }) {
  return (
    <TableRow>
      {Array.from({ length: cols }).map((_, i) => (
        <TableCell key={i}>
          <Skeleton className="h-4 w-full" />
        </TableCell>
      ))}
    </TableRow>
  )
}

export function LocationsSummaryTable({ data, isLoading, onRowClick, startDates }: Props) {
  const [sortColumn, setSortColumn] = useState<SortColumn>('analyses')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')

  function handleSort(column: SortColumn) {
    if (column === sortColumn) {
      // Same column → toggle direction
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      // New column → reset to a sensible default direction:
      // text columns ASC (alphabetical), numeric columns DESC (biggest first)
      setSortColumn(column)
      setSortDirection(column === 'name' ? 'asc' : 'desc')
    }
  }

  const sortedData = useMemo(() => {
    const compareFn = comparators[sortColumn]
    const dir = sortDirection === 'asc' ? 1 : -1
    return [...data].sort((a, b) => {
      const av = compareFn(a)
      const bv = compareFn(b)
      if (av < bv) return -1 * dir
      if (av > bv) return 1 * dir
      return 0
    })
  }, [data, sortColumn, sortDirection])

  function header(label: string, column: SortColumn, align: 'left' | 'right' = 'left') {
    const active = sortColumn === column
    return (
      <TableHead
        className={`cursor-pointer select-none hover:text-foreground ${align === 'right' ? 'text-right' : ''}`}
        onClick={() => handleSort(column)}
      >
        {label}
        <SortIcon active={active} direction={sortDirection} />
      </TableHead>
    )
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            {header('Location', 'name')}
            {header('Analyses', 'analyses', 'right')}
            {header('Bookings', 'bookings', 'right')}
            {header('Booking Rate', 'bookingRate', 'right')}
            {header('AI Cost', 'cost', 'right')}
            {header('Tokens', 'tokens', 'right')}
            {header('Pending', 'pendingFollowups', 'right')}
            {header('Last Activity', 'lastActivity')}
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading && Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)}

          {!isLoading && sortedData.length === 0 && (
            <TableRow>
              <TableCell colSpan={8} className="py-12">
                <div className="flex flex-col items-center justify-center text-muted-foreground">
                  <MapPin className="h-10 w-10 mb-3 opacity-30" />
                  <p className="font-medium">No locations found</p>
                  <p className="text-xs mt-1">
                    Try widening the time range above.
                  </p>
                </div>
              </TableCell>
            </TableRow>
          )}

          {!isLoading &&
            sortedData.map((loc) => {
              const cost = calculateTotalCost(loc.total_input_tokens, loc.total_output_tokens)
              const totalTokens = loc.total_input_tokens + loc.total_output_tokens
              const bookingRate =
                loc.analysis_count > 0
                  ? (loc.bookings_detected / loc.analysis_count) * 100
                  : null

              const startDate = startDates?.get(loc.location_id) ?? null

              return (
                <TableRow
                  key={loc.location_id}
                  className={`cursor-pointer ${startDate ? 'bg-muted text-muted-foreground hover:bg-muted/80' : 'hover:bg-muted/50'}`}
                  onClick={() => onRowClick(loc.location_id)}
                >
                  <TableCell className="font-medium">
                    <div className="truncate max-w-[260px]" title={loc.name}>
                      {loc.name || (
                        <span className="font-mono text-xs text-muted-foreground">
                          {truncateId(loc.location_id)}
                        </span>
                      )}
                    </div>
                    {startDate && (
                      <div className="text-xs font-normal text-foreground">Cut off: {formatStartDate(startDate)}</div>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {loc.analysis_count.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    <span className={loc.bookings_detected > 0 ? 'font-medium text-emerald-700' : ''}>
                      {loc.bookings_detected}
                    </span>
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-sm">
                    {bookingRate === null ? (
                      <span className="text-muted-foreground">—</span>
                    ) : (
                      `${bookingRate.toFixed(1)}%`
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{formatCost(cost)}</TableCell>
                  <TableCell className="text-right tabular-nums text-xs text-muted-foreground">
                    {formatTokenCount(totalTokens)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {loc.pending_followups > 0 ? (
                      <span className="font-medium text-amber-700">{loc.pending_followups}</span>
                    ) : (
                      <span className="text-muted-foreground">0</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                    {loc.last_analysis_at ? formatRelativeTime(loc.last_analysis_at) : '—'}
                  </TableCell>
                </TableRow>
              )
            })}
        </TableBody>
      </Table>
    </div>
  )
}
